import { useEffect, useState } from "react";

// Responses kept for the whole visit, so navigating back to a page doesn't refetch.
// It lives in memory: a page reload starts empty and downloads fresh data.
const responseCache = new Map();

// Fetches `url` and parses the body as JSON (default) or text.
// An empty url is skipped, so callers can pass a url that is still loading.
// Pass `cache: false` for live data that must be fetched on every mount.
export const useFetch = (url, { as = "json", cache = true } = {}) => {
  const isCached = cache && responseCache.has(url);

  // Start from the cached value, so a cached page renders full on its first render
  const [data, setData] = useState(() => isCached ? responseCache.get(url) : null);
  const [loading, setLoading] = useState(!isCached);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return;

    if (cache && responseCache.has(url)) {
      setData(responseCache.get(url));
      setLoading(false);
      setError(null);
      return;
    }

    // Ignore the response if the url changed or the component unmounted meanwhile
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const body = as === "text" ? await response.text() : await response.json();
        if (cache) responseCache.set(url, body);
        if (!ignore) setData(body);
      } catch (err) {
        if (!ignore) setError(err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();

    return () => { ignore = true; };
  }, [url, as, cache]);

  return { data, loading, error };
};
